import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { LogIn, Home, Store, Plus, LayoutList, LogOut, Languages, Mountain, Info, Shield, Heart, MessageCircle, BadgeCheck, Crown, Sparkles } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const { subscription, isActive, daysLeft } = useSubscription();
  const nav = useNavigate();

  const linkCls = ({ isActive }) =>
    `text-sm font-medium px-3 py-2 rounded-md transition-colors ${
      isActive ? "text-primary bg-accent" : "text-foreground/70 hover:text-primary hover:bg-accent/60"
    }`;

  return (
    <header
      className="sticky top-0 z-40 border-b border-border/60 backdrop-blur-xl bg-background/85"
      data-testid="app-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-brand">
          <span className="w-9 h-9 rounded-lg bg-primary text-primary-foreground grid place-items-center transition-transform group-hover:-translate-y-0.5">
            <Mountain size={18} />
          </span>
          <div className="leading-tight">
            <div className="wordmark text-xl leading-none">
              Bhoomi<span className="wordmark-dot"></span><span className="wordmark-mitra">Mitra</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground -mt-0.5 hidden sm:block">
              {t.brandDevanagari} • Property Mitra
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">
            <Home size={16} className="inline mr-1.5 -mt-0.5" />{t.nav.home}
          </NavLink>
          <NavLink to="/browse" className={linkCls} data-testid="nav-browse">
            <Store size={16} className="inline mr-1.5 -mt-0.5" />{t.nav.browse}
          </NavLink>
          <NavLink to="/sell" className={linkCls} data-testid="nav-sell">
            <Plus size={16} className="inline mr-1.5 -mt-0.5" />{t.nav.sell}
          </NavLink>
          {user && (
            <NavLink to="/my-listings" className={linkCls} data-testid="nav-mine">
              <LayoutList size={16} className="inline mr-1.5 -mt-0.5" />{t.nav.myListings}
            </NavLink>
          )}
          {user && (
            <NavLink to="/my-enquiries" className={linkCls} data-testid="nav-my-enquiries">
              <MessageCircle size={16} className="inline mr-1.5 -mt-0.5" />{t.nav.myEnquiries}
            </NavLink>
          )}
          <NavLink to="/favorites" className={linkCls} data-testid="nav-favorites">
            <Heart size={16} className="inline mr-1.5 -mt-0.5" />Favorites
          </NavLink>
          {user && (
            <NavLink to="/kyc" className={linkCls} data-testid="nav-kyc">
              <BadgeCheck size={16} className="inline mr-1.5 -mt-0.5" />KYC
            </NavLink>
          )}
          <NavLink to="/about" className={linkCls} data-testid="nav-about">
            <Info size={16} className="inline mr-1.5 -mt-0.5" />{t.nav.about}
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
              <Shield size={16} className="inline mr-1.5 -mt-0.5" />Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <Link to="/plans" data-testid="nav-plans">
              {isActive ? (
                <span className={`hidden md:inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full ${
                  subscription?.plan === "builder_monthly" ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-secondary/20 text-secondary-foreground border border-secondary/50"
                }`}>
                  <Crown size={11} />
                  {subscription?.plan === "builder_monthly" ? (lang === "hi" ? "बिल्डर" : "Builder") :
                   subscription?.plan === "broker_yearly" ? (lang === "hi" ? "ब्रोकर" : "Broker") :
                   (lang === "hi" ? "प्लान" : "Plan")}
                  {daysLeft != null && <span className="opacity-80">• {daysLeft}d</span>}
                </span>
              ) : (
                <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-secondary text-secondary-foreground shadow-sm hover:opacity-90">
                  <Sparkles size={11} />{lang === "hi" ? "प्लान देखें" : "View Plans"}
                </span>
              )}
            </Link>
          )}
          <button
            onClick={toggle}
            className="text-xs font-semibold px-3 py-2 rounded-full border border-border hover:bg-accent transition-colors flex items-center gap-1.5"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Languages size={14} />
            {lang === "en" ? "हिं" : "EN"}
          </button>
          {user ? (
            <>
              <Link to="/profile" className="hidden sm:flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="nav-profile-link">
                {user.photo_url ? (
                  <img src={user.photo_url} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-border" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/15 text-primary grid place-items-center text-xs font-bold">
                    {(user.name || "?")[0]?.toUpperCase()}
                  </div>
                )}
                <span className="text-sm font-medium text-muted-foreground max-w-[100px] truncate">
                  {user.name}
                </span>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="nav-logout-btn"
              >
                <LogOut size={14} className="mr-1" />{t.nav.logout}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="nav-login-btn">
                  <LogIn size={14} className="mr-1" />{t.nav.login}
                </Button>
              </Link>
              <Link to="/register" className="hidden sm:block">
                <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="nav-register-btn">
                  {t.nav.register}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
